import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Checkbox from '@mui/material/Checkbox'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'

import Loading from '../loading/Loading'

import { initializeTags, retryTags } from '../../reducers/tagsReducers'
import { setErrorNotification, setSuccessNotification } from '../../reducers/notificationReducer'
import { setUser } from '../../reducers/userReducer'
import quizService from '../../services/quiz'
import userService from '../../services/user'
import { PAGES } from '../navigation/routes'
import { QuizStyle } from '../styles'

const renderSelectedTagChips = (selectedIds, tags) => {
    if (selectedIds.length === 0) return null

    return (
        <Box sx={QuizStyle.selectedChipsContainer}>
            <Box sx={QuizStyle.chipsRow}>
                {selectedIds.map(id => {
                    const tag = tags.find(currentTag => currentTag.id === id)
                    return tag ? <Chip key={id} label={tag.name} size="small" color="primary" variant="outlined" /> : null
                })}
            </Box>
        </Box>
    )
}
const PENDING_QUIZ_KEY = 'TCAIREMPendingQuiz'
const QUIZ_TAG_TYPES = ['experience', 'contentCategory', 'demographic']

const Quiz = () => {
    const user = useSelector(state => state.user)
    const tagsByType = useSelector(state => state.tags || {})
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [step, setStep] = useState(1)
    const [selectedExperience, setSelectedExperience] = useState(null)
    const [selectedInterests, setSelectedInterests] = useState([])
    const [selectedDemographics, setSelectedDemographics] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [showSavePrompt, setShowSavePrompt] = useState(false)
    const pendingQuizProcessed = useRef(false)
    // Saved answers for a logged-in user: 'loading' | 'loaded' | 'error'. A failed load must
    // never look like a first-time user: submit replaces all three lists server-side, so a
    // blank wizard over a failed load silently wipes what was saved (issue #408).
    const [savedStatus, setSavedStatus] = useState('loading')
    const [loadAttempt, setLoadAttempt] = useState(0)

    const isAnon = !user || user.anon

    const experienceTags = tagsByType?.experience || []
    const interestCategories = tagsByType?.contentCategory || []
    const demographicTags = tagsByType?.demographic || []

    // Require arrays, not just "not undefined": a failed fetch marks its type null
    // (#384), and that must not be treated as loaded taxonomy data.
    const tagsLoaded = (
        Array.isArray(tagsByType?.experience) &&
        Array.isArray(tagsByType?.contentCategory) &&
        Array.isArray(tagsByType?.demographic)
    )

    const failedTagTypes = QUIZ_TAG_TYPES.filter(t => tagsByType?.[t] === null)

    useEffect(() => {
        if (tagsByType?.experience === undefined) dispatch(initializeTags('experience'))
        if (tagsByType?.contentCategory === undefined) dispatch(initializeTags('contentCategory'))
        if (tagsByType?.demographic === undefined) dispatch(initializeTags('demographic'))
        if (tagsByType?.experience && tagsByType?.contentCategory && tagsByType?.demographic) {
            setLoading(false)
        }
    }, [dispatch, tagsByType])

    // Auto-save pending quiz results when a user logs in and returns to this page
    useEffect(() => {
        if (user && !user.anon && !pendingQuizProcessed.current) {
            const pending = sessionStorage.getItem(PENDING_QUIZ_KEY)
            if (pending) {
                const savePending = async () => {
                    try {
                        const pendingResults = JSON.parse(pending)
                        await quizService.saveQuizResults(pendingResults)
                        pendingQuizProcessed.current = true
                        sessionStorage.removeItem(PENDING_QUIZ_KEY)
                        const updatedUser = await userService.getCurrent()
                        dispatch(setUser(updatedUser))
                        dispatch(setSuccessNotification('Quiz results saved! Here are your recommended resources.'))
                        navigate(PAGES.recommended.path)
                    } catch {
                        dispatch(setErrorNotification('Error saving quiz results, please try again'))
                        setLoading(false)
                    }
                }
                savePending()
                return
            }
        }
    }, [user, dispatch, navigate])

    // Pre-populate with existing quiz results when tags are loaded (logged-in users only)
    useEffect(() => {
        if (!user || user.anon || !tagsLoaded) return

        let cancelled = false
        const loadExisting = async () => {
            setSavedStatus('loading')
            try {
                const results = await quizService.getQuizResults()
                if (cancelled) return
                // A first-time user is a 200 with empty lists; the endpoint has no 404, so
                // any failure here is a genuine error, not "no saved answers" (issue #408).
                setSelectedExperience(results.experience_tags?.[0]?.id ?? null)
                setSelectedInterests((results.interest_categories || []).map(cat => cat.id))
                setSelectedDemographics((results.demographic_tags || []).map(tag => tag.id))
                setSavedStatus('loaded')
            } catch {
                if (!cancelled) setSavedStatus('error')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        loadExisting()
        return () => { cancelled = true }
    }, [user, tagsLoaded, loadAttempt])

    // For anonymous users, stop loading once tags are available
    useEffect(() => {
        if (isAnon && tagsLoaded) {
            setLoading(false)
        }
    }, [isAnon, tagsLoaded])

    const toggleSelection = (id, setSelectedTags) => {
        setSelectedTags(previous =>
            previous.includes(id) ? previous.filter(currentId => currentId !== id) : [...previous, id]
        )
    }

    const handleNext = () => {
        if (step === 1 && selectedExperience === null) {
            dispatch(setErrorNotification('Please select an experience level'))
            return
        }

        if (step === 2 && selectedInterests.length === 0) {
            dispatch(setErrorNotification('Please select at least one area of interest'))
            return
        }

        setStep(currentStep => Math.min(currentStep + 1, 3))
    }

    const handleBack = () => {
        setStep(currentStep => Math.max(currentStep - 1, 1))
    }

    const handleSubmit = async () => {
        if (selectedExperience === null) {
            dispatch(setErrorNotification('Please select an experience level'))
            return
        }

        if (selectedInterests.length === 0) {
            dispatch(setErrorNotification('Please select at least one area of interest'))
            return
        }

        // Anonymous users: show save prompt instead of saving
        if (isAnon) {
            sessionStorage.setItem(PENDING_QUIZ_KEY, JSON.stringify({
                experience_tags: [selectedExperience],
                interest_categories: selectedInterests,
                demographic_tags: selectedDemographics,
            }))
            setShowSavePrompt(true)
            return
        }

        setSaving(true)
        try {
            await quizService.saveQuizResults({
                experience_tags: [selectedExperience],
                interest_categories: selectedInterests,
                demographic_tags: selectedDemographics,
            })
            const updatedUser = await userService.getCurrent()
            dispatch(setUser(updatedUser))
            dispatch(setSuccessNotification('Quiz results saved! Here are your recommended resources.'))
            navigate(PAGES.recommended.path)
        } catch {
            dispatch(setErrorNotification('Error saving quiz results, please try again'))
        } finally {
            setSaving(false)
        }
    }

    if (failedTagTypes.length > 0) {
        return (
            <Container maxWidth="md" sx={QuizStyle.container}>
                <Alert severity="error" action={
                    <Button color="inherit" size="small"
                        onClick={() => failedTagTypes.forEach(t => dispatch(retryTags(t)))}>
                        Retry
                    </Button>
                }>
                    We couldn't load the quiz questions. Check your connection and try again.
                </Alert>
            </Container>
        )
    }

    const awaitingSaved = !isAnon && savedStatus === 'loading'
    if (!user || loading || awaitingSaved) return <Loading />

    if (!isAnon && savedStatus === 'error') {
        return (
            <Container maxWidth="md" sx={QuizStyle.container}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => setLoadAttempt(n => n + 1)}>
                            Retry
                        </Button>
                    }
                >
                    We couldn't load your saved quiz answers, so the quiz is paused to avoid
                    overwriting them. Please try again.
                </Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="md" sx={QuizStyle.container}>
            <Typography variant="h4" component="h1" gutterBottom sx={QuizStyle.title}>
                Resource Recommendation Quiz
            </Typography>
            <Typography variant="body1" gutterBottom sx={QuizStyle.subtitle}>
                Answer a few quick questions so we can recommend the best resources for your experience, interests, and role.
            </Typography>

            {step === 1 && !showSavePrompt && (
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ ...QuizStyle.stepHeading, ...QuizStyle.stepHeadingStep1 }}>
                        Step 1 of 3: What is your experience level with AI in medicine?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={QuizStyle.stepSubheading}>
                        Select one.
                    </Typography>
                    <RadioGroup
                        value={selectedExperience ?? ''}
                        onChange={(event) => setSelectedExperience(Number(event.target.value))}
                    >
                        <Grid container spacing={2}>
                            {experienceTags.map(tag => (
                                <Grid item xs={12} sm={4} key={tag.id}>
                                    <Box
                                        sx={QuizStyle.experienceOption(selectedExperience === tag.id)}
                                        onClick={() => setSelectedExperience(tag.id)}
                                    >
                                        <FormControlLabel
                                            value={tag.id}
                                            control={<Radio onClick={event => event.stopPropagation()} />}
                                            label={tag.name}
                                            sx={QuizStyle.experienceLabel}
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </RadioGroup>
                    {experienceTags.length === 0 && (
                        <Typography color="text.secondary">No experience levels available yet.</Typography>
                    )}
                    <Box sx={QuizStyle.nextButtonContainer}>
                        <Button variant="contained" onClick={handleNext} disabled={experienceTags.length === 0 || selectedExperience === null}>
                            Next
                        </Button>
                    </Box>
                </Box>
            )}

            {step === 2 && !showSavePrompt && (
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ ...QuizStyle.stepHeading, ...QuizStyle.stepHeadingStep2 }}>
                        Step 2 of 3: What areas of AI in medicine interest you?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={QuizStyle.stepSubheadingStep2}>
                        Select all that apply.
                    </Typography>

                    <FormGroup>
                        <Grid container spacing={1}>
                            {interestCategories.map(category => (
                                <Grid item xs={12} sm={6} md={4} key={category.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedInterests.includes(category.id)}
                                                onChange={() => toggleSelection(category.id, setSelectedInterests)}
                                            />
                                        }
                                        label={category.name}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    {interestCategories.length === 0 && (
                        <Typography color="text.secondary">No interest areas available yet.</Typography>
                    )}

                    {selectedInterests.length > 0 && (
                        <Box sx={QuizStyle.selectedChipsContainer}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Selected interests:
                            </Typography>
                            {renderSelectedTagChips(selectedInterests, interestCategories)}
                        </Box>
                    )}

                    <Box sx={QuizStyle.navButtonContainer}>
                        <Button variant="outlined" onClick={handleBack}>
                            Back
                        </Button>
                        <Button variant="contained" onClick={handleNext} disabled={interestCategories.length === 0 || selectedInterests.length === 0}>
                            Next
                        </Button>
                    </Box>
                </Box>
            )}

            {step === 3 && !showSavePrompt && (
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ ...QuizStyle.stepHeading, ...QuizStyle.stepHeadingStep2 }}>
                        Step 3 of 3: Which demographics best describe you?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={QuizStyle.stepSubheadingStep2}>
                        This step is optional. Select all that apply, and we will match resources tagged for any of your selected demographics.
                    </Typography>

                    <FormGroup>
                        <Grid container spacing={1}>
                            {demographicTags.map(tag => (
                                <Grid item xs={12} sm={6} md={4} key={tag.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedDemographics.includes(tag.id)}
                                                onChange={() => toggleSelection(tag.id, setSelectedDemographics)}
                                            />
                                        }
                                        label={tag.name}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    {demographicTags.length === 0 && (
                        <Typography color="text.secondary">
                            No demographic options are available yet. You can skip this step and still save your quiz.
                        </Typography>
                    )}

                    {selectedDemographics.length > 0 && (
                        <Box sx={QuizStyle.selectedChipsContainer}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Selected demographics:
                            </Typography>
                            {renderSelectedTagChips(selectedDemographics, demographicTags)}
                        </Box>
                    )}

                    <Box sx={QuizStyle.navButtonContainer}>
                        <Button variant="outlined" onClick={handleBack}>
                            Back
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Saving...' : 'See My Recommendations'}
                        </Button>
                    </Box>
                </Box>
            )}

            {showSavePrompt && (
                <Box sx={QuizStyle.savePromptContainer}>
                    <Typography variant="h5" gutterBottom sx={QuizStyle.stepHeading}>
                        Want to save your results?
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Log in or create an account to save your quiz results, or continue without saving to view recommendations now.
                    </Typography>
                    <Box sx={QuizStyle.savePromptButtons}>
                        <Button variant="outlined" onClick={() => navigate(PAGES.login.path, { state: { from: PAGES.quiz.path } })}>
                            Log In
                        </Button>
                        <Button variant="contained" onClick={() => navigate(PAGES.sign_up.path)}>
                            Sign Up
                        </Button>
                        <Button variant="outlined" onClick={() => navigate(PAGES.recommended.path)}>
                            Continue Without Saving
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '16px' }}>
                        Your selections will be saved automatically after you log in.
                    </Typography>
                </Box>
            )}
        </Container>
    )
}

export default Quiz
